import { forwardRef, type AnchorHTMLAttributes, type MouseEvent } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  href: string;
  replace?: boolean;
  scroll?: boolean;
  prefetch?: boolean;
};

function isExternalHref(href: string) {
  return /^(https?:|mailto:|tel:)/.test(href);
}

const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ href, replace = false, onClick, children, ...props }, ref) => {
    const navigate = useNavigate();

    if (isExternalHref(href)) {
      return (
        <a ref={ref} href={href} onClick={onClick} {...props}>
          {children}
        </a>
      );
    }

    function handleClick(event: MouseEvent<HTMLAnchorElement>) {
      onClick?.(event);
      if (event.defaultPrevented) return;
      if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      navigate(href, { replace });
    }

    return (
      <RouterLink ref={ref} to={href} replace={replace} onClick={handleClick} {...props}>
        {children}
      </RouterLink>
    );
  },
);

Link.displayName = 'NextCompatLink';

export default Link;
